import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import grey from '@mui/material/colors/grey';
import { TransitionProps } from '@mui/material/transitions';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { useQuery } from '@tanstack/react-query';
import { API } from 'aws-amplify';
import { orderBy } from 'lodash';
import React from 'react';
import { ReactComponent as CollapseIcon } from '../../../assets/collapse.svg';
import { ReactComponent as ExpandIcon } from '../../../assets/expand.svg';
import { AnimateIn } from '../../../components/animate-in';
import { captureError } from '../../../utils/capture-error';
import { HotspotData, MapHotspot } from '../../business/crm/map-hotspot';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export function HotSpotsMap(props: {
  height: number;
  isMobile: boolean;
  open: boolean;
  onOpen: () => void;
}) {
  // Props
  const { height, isMobile, open } = props;
  // Ref
  const mapRef = React.useRef<GoogleMap>(null);
  // State
  const [loading, setLoading] = React.useState(true);
  const [expand, setExpand] = React.useState(false);
  const [userLocation, setUserLocation] =
    React.useState<GeolocationCoordinates>();

  // Query - Hotspots
  const path = '/meetings';
  const query = useQuery({
    queryKey: [path],
    queryFn: async () => {
      const response: {
        data: HotspotData[];
      } = await API.post('DashboardAPI', '/meetings', {});

      if (response.data) {
        return response.data
          .map((meeting) => ({
            ...meeting,
            Latitude: Number(meeting.Latitude),
            Longitude: Number(meeting.Longitude),
          }))
          .filter(
            (i) =>
              i.Longitude &&
              i.Longitude !== 0 &&
              i.Latitude &&
              i.Latitude !== 0,
          );
      }

      return [];
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // Get user's location
  React.useEffect(() => {
    const getCurrentPosition = () => {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (successCallback) => {
            resolve(successCallback);
          },
          (errorCallback) => {
            reject(errorCallback);
          },
        );
      });
    };
    const fetchData = async () => {
      try {
        setLoading(true);
        // Alliance Convention Center
        // @ts-ignore
        const defaultCoordinates: GeolocationCoordinates = {
          accuracy: 10,
          altitude: null,
          altitudeAccuracy: null,
          heading: null,
          latitude: 36.07813,
          longitude: -79.432279,
          speed: null,
        };

        if ('geolocation' in navigator) {
          // Coordinates
          try {
            const position =
              (await getCurrentPosition()) as GeolocationPosition;
            setUserLocation(position.coords);
          } catch (error) {
            // Permission was denied
            setUserLocation(defaultCoordinates);
          }
        } else {
          /* geolocation IS NOT available */
          setUserLocation(defaultCoordinates);
        }
      } catch (error) {
        captureError({ data: { error } });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_MAPS_API_KEY || '',
  });

  // Close mobile dialog
  const handleClose = () => {
    props.onOpen();
  };

  let dataHotspots: HotspotData[] = query.data || [];

  // Coordinates for centering the map
  // Default to coordinates for the Alliance Convention Center
  const center = { lat: 36.07813, lng: -79.432279 };
  if (userLocation) {
    // center map based on user's location
    center.lat = userLocation.latitude;
    center.lng = userLocation.longitude;
  }

  // Hotspot Meetings grouped by location
  const displayHotspots = orderBy(dataHotspots, 'DateTime', 'asc').filter(
    (i) => i.Longitude && i.Longitude !== 0 && i.Latitude && i.Latitude !== 0,
  );

  const coords = React.useMemo(() => {
    const map = new Map<
      string,
      { lat: number; lng: number; meetings: HotspotData[] }
    >();

    displayHotspots.forEach((h) => {
      const key = `${h.Latitude}-${h.Longitude}`;

      if (!map.has(key) && h.Latitude !== null && h.Longitude !== null) {
        map.set(key, {
          lat: h.Latitude,
          lng: h.Longitude,
          meetings: [],
        });
      }

      map.get(key)!.meetings.push(h);
    });

    return Array.from(map.values());
  }, [displayHotspots]);

  const content = (
    <React.Fragment>
      <Box
        sx={{
          backgroundColor: '#2F3439',
          height: isMobile ? '100%' : height,
          width: '100%',
        }}
      >
        {isLoaded && !loading ? (
          <GoogleMap
            ref={mapRef}
            mapContainerStyle={{ height: '100%', width: '100%' }}
            center={center}
            zoom={10}
            options={{
              fullscreenControl: false,
              zoomControlOptions: {
                position: 6,
              },
              streetViewControlOptions: {
                position: 6,
              },
              mapTypeControlOptions: {
                position: 3,
              },
            }}
          >
            {isMobile && (
              <Button
                size="small"
                variant="contained"
                aria-label={expand ? 'collapse' : 'expand'}
                sx={{
                  position: 'absolute',
                  right: 10,
                  top: 65,
                  zIndex: 5,
                  backgroundColor: '#fff',
                  maxWidth: '40px',
                  maxHeight: '40px',
                  minWidth: '40px',
                  minHeight: '40px',
                }}
                onClick={() => setExpand((currentState) => !currentState)}
              >
                {!expand ? (
                  <ExpandIcon style={{ width: 20 }} />
                ) : (
                  <CollapseIcon style={{ width: 20 }} />
                )}
              </Button>
            )}

            {coords.map((coord) => (
              <MapHotspot
                key={`${coord.lat}-${coord.lng}`}
                meetings={coord.meetings}
              />
            ))}
          </GoogleMap>
        ) : (
          <Box
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress color="info" size={64} />
          </Box>
        )}
      </Box>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      {isMobile ? (
        <Dialog fullScreen open={open} TransitionComponent={Transition}>
          {!expand && (
            <DialogTitle
              sx={{
                background:
                  'linear-gradient(180deg, #F6921E 0%, #CE0E2D 100%);',

                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `2px solid ${grey[900]}`,
              }}
            >
              <Typography
                component="span"
                variant="h6"
                sx={{
                  color: '#fff',
                  fontFamily: 'Bebas Neue',
                  fontSize: 28,
                  textShadow: '2px 2px #000',
                }}
              >
                HotSpots Map
              </Typography>
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{
                  boxShadow: '0px 0px 4px 0px #fff',
                }}
              >
                <Close fontSize="small" sx={{ color: '#fff' }} />
              </IconButton>
            </DialogTitle>
          )}
          {content}
        </Dialog>
      ) : (
        <AnimateIn to="top">
          <Paper
            sx={{
              height: height,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            {content}
          </Paper>
        </AnimateIn>
      )}
    </React.Fragment>
  );
}
